/* eslint-disable react-hooks/set-state-in-effect */
import { Modal, TextField } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { Tag } from "@features/clinic-patients/clinic/types";
import { useEffect, useState } from "react";
import TagComponent from "./Tag";
import SwatchColorPicker from "../SwatchColorPicker/SwatchColorPicker";
import { getBackgroundByName, SWATCH_COLORS } from "../SwatchColorPicker/swatchColors";

interface CreateTagModalProps {
  open: boolean;
  onClose: () => void;
  onAddTag: (tag: Omit<Tag, "id">) => void;
  isSubmitting: boolean;
  editTag?: Tag | null;
  isDuplicating?: boolean;
}

export function CreateTagModal({
  open,
  onClose,
  onAddTag,
  isSubmitting,
  editTag,
  isDuplicating = false,
}: CreateTagModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  // Store color as swatch name
  const [color, setColor] = useState<string>(SWATCH_COLORS[0].name);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (editTag) {
      setName(editTag.name);
      // If color is a swatch name, use it; otherwise fallback to first swatch
      if (editTag.color && getBackgroundByName(editTag.color)) {
        setColor(editTag.color);
      } else {
        setColor(SWATCH_COLORS[0].name);
      }
    }
  }, [editTag]);

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);

    if (name.trim()) {
      onAddTag({
        name: name.trim(),
        color: color, // store swatch name
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName("");
      setColor(SWATCH_COLORS[0].name);
      setHasAttemptedSubmit(false);
      onClose();
    }
  };

  const getModalTitle = () => {
    if (isDuplicating) return t("tags.duplicateTag");
    if (editTag) return t("tags.editTag");
    return t("tags.createNewTag");
  };

  const getPrimaryActionContent = () => {
    if (isDuplicating) return t("tags.duplicate");
    if (editTag) return t("common.update");
    return t("common.create");
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={getModalTitle()}
      primaryAction={{
        content: getPrimaryActionContent(),
        onAction: handleSubmit,
        loading: isSubmitting,
        disabled: isSubmitting || (hasAttemptedSubmit && !name.trim()),
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleClose,
          disabled: isSubmitting,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <div style={{ gap: "16px", display: "flex", flexDirection: "column" }}>
          <TextField
            label={t("tags.name")}
            value={name}
            onChange={setName}
            autoComplete="off"
            error={
              hasAttemptedSubmit && !name.trim()
                ? t("tags.nameRequired")
                : undefined
            }
            requiredIndicator
          />

          <div>
            <div style={{ marginBottom: "4px" }}>{t("tags.color")}</div>
            <SwatchColorPicker value={color} onChange={setColor} />
          </div>

          <div>
            <div style={{ marginBottom: "4px" }}>{t("tags.preview")}</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <TagComponent tagLabel={name || t("tags.preview")} colorName={color} />
            </div>
          </div>
        </div>
      </Modal.Section>
    </Modal>
  );
}
